// ---------------------------------------------------------------------------
// Test Mode — mock API router (CLE-250).
//
// When Test Mode is ON, `apiFetch` delegates here instead of calling the real
// backend. We match the request path (and method) against the simulated
// dataset and return the same response envelopes the real routes would, so
// every page renders the Clean Code Culture SRL demo data with no other code
// changes. Writes (POST/PUT/PATCH/DELETE) return a benign success so the UI
// never errors — the real database is never touched.
//
// Unmatched GETs fall back to an empty-but-valid envelope so unknown widgets
// degrade gracefully rather than throwing.
// ---------------------------------------------------------------------------

import * as data from '../testData';
import { ApiError } from './api';
// Type-only: a value import here closes an import cycle (permissionsApi →
// useAuthStore → api → mockRouter) and the app fails to boot.
import { MODULE_CATALOG, type ModuleKey } from './moduleCatalog';

interface ParsedRequest {
  pathname: string;
  query: URLSearchParams;
  method: string;
}

function parse(path: string, init?: RequestInit): ParsedRequest {
  const [rawPath, rawQuery = ''] = path.split('?');
  return {
    pathname: rawPath,
    query: new URLSearchParams(rawQuery),
    method: (init?.method ?? 'GET').toUpperCase(),
  };
}

// Standard envelopes.
function ok<T>(payload: T): { success: true; data: T } {
  return { success: true, data: payload };
}
function paged<T>(items: T[], total = items.length) {
  return {
    success: true as const,
    data: items,
    meta: { page: 1, limit: Math.max(total, 1), total, totalPages: 1 },
  };
}

// Pull a trailing id segment from a path like /api/projects/pr1.
function lastSegment(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  return parts[parts.length - 1] ?? '';
}

// --- Demo task timers ------------------------------------------------------
// In memory, for the session: the real timer endpoints' behaviour without a
// server. A Stop writes no time entry (the demo's worklogs are fixed data).

interface DemoTimer {
  id: string;
  taskId: string;
  taskTitle: string | null;
  taskIdentifier: string | null;
  startedAt: string;
  runningSince: string | null;
  accumulatedSeconds: number;
}

const demoTimers = new Map<string, DemoTimer>();

function demoTimerWrite(pathname: string): unknown {
  const match = /^\/api\/tasks\/([^/]+)\/timer\/(start|pause|resume|stop)$/.exec(pathname);
  if (!match) return undefined;
  const [, taskId, action] = match;
  const now = new Date();
  const timer = demoTimers.get(taskId);

  if (action === 'start') {
    if (timer) throw new ApiError(409, 'A timer is already running on this task', 'TIMER_RUNNING');
    const task = data.findTask(taskId);
    const started: DemoTimer = {
      id: `demo-timer-${taskId}`,
      taskId,
      taskTitle: task?.title ?? null,
      taskIdentifier: task?.identifier ?? null,
      startedAt: now.toISOString(),
      runningSince: now.toISOString(),
      accumulatedSeconds: 0,
    };
    demoTimers.set(taskId, started);
    return { timer: { ...started } };
  }
  if (!timer) throw new ApiError(404, 'No timer on this task', 'TIMER_NOT_FOUND');
  if (action === 'pause' && timer.runningSince) {
    timer.accumulatedSeconds += Math.floor((now.getTime() - Date.parse(timer.runningSince)) / 1000);
    timer.runningSince = null;
  }
  if (action === 'resume' && !timer.runningSince) timer.runningSince = now.toISOString();
  if (action === 'stop') {
    demoTimers.delete(taskId);
    return { stopped: true, entry: null };
  }
  return { timer: { ...timer } };
}

// --- Route table -----------------------------------------------------------
// Each entry is a matcher returning the response body, or `undefined` to fall
// through to the next matcher. Order matters (more specific paths first).

type Handler = (req: ParsedRequest) => unknown;

const GET_ROUTES: Array<[RegExp, Handler]> = [
  // Push notifications: the demo account has no service worker talking to a
  // real push service, so the panel reports the feature as unconfigured and
  // shows its explanation instead of a toggle that could not work.
  [/^\/api\/push\/config$/, () => ok({ enabled: false, publicKey: null, devices: [] })],
  [
    /^\/api\/push\/preferences$/,
    () => ok({ preferences: { tasks: true, chat: true, voice: true, meetings: true } }),
  ],
  // Settings → Alerte read `open.length`; the empty fallback crashed the tab.
  [/^\/api\/alerts$/, () => ok(demoAlerts())],
  [/^\/api\/alerts\/summary$/, () => ok({ open: 1, unacknowledged: 1 })],
  // Settings → Istoric emailuri mapped `data` / `byTemplate`; the empty
  // fallback crashed the tab. `/stats` before the list, like the real mount.
  [
    /^\/api\/admin\/emails\/stats$/,
    () =>
      ok({
        totalSent: 42,
        totalOpened: 29,
        openRate: 69,
        byTemplate: [
          { template: 'invoice-reminder', sent: 18, opened: 13, openRate: 72.2 },
          { template: 'daily-report', sent: 20, opened: 14, openRate: 70 },
          { template: 'welcome', sent: 4, opened: 2, openRate: 50 },
        ],
      }),
  ],
  [/^\/api\/admin\/emails$/, () => paged(demoEmails())],
  // Settings → Module listed nothing in demo: every module on, two core ones.
  [/^\/api\/modules$/, () => ok(demoModules([]))],
  // Every upload hint reads this; the empty fallback printed "max. NaN MB".
  [
    /^\/api\/settings\/upload-limit$/,
    () =>
      ok({
        maxUploadBytes: 100 * 1024 * 1024,
        hardCapBytes: 2048 * 1024 * 1024,
        minBytes: 1024 * 1024,
      }),
  ],
  // Firmio Meet. Joining is off in Test Mode (the public preview answers
  // `livekitConfigured:false`), so only the reads exist; `/public` before
  // the bare `/:id`, like the real mount order.
  [
    /^\/api\/meet$/,
    (r) =>
      ok({
        meetings: data.buildVideoMeetings({
          tab: r.query.get('tab'),
          from: r.query.get('from'),
          to: r.query.get('to'),
        }),
        transcriptionEnabled: true,
      }),
  ],
  [
    /^\/api\/meet\/public\/[^/]+$/,
    (r) => {
      const info = data.buildMeetPublic(lastSegment(r.pathname));
      if (!info) throw new ApiError(404, 'Meeting not found', 'MEETING_NOT_FOUND');
      return ok(info);
    },
  ],
  [/^\/api\/meet\/[^/]+\/waiting$/, () => ok({ waiting: [] })],
  [
    /^\/api\/meet\/[^/]+$/,
    (r) => {
      const meeting = data.buildVideoMeeting(lastSegment(r.pathname));
      if (!meeting) throw new ApiError(404, 'Meeting not found', 'MEETING_NOT_FOUND');
      return ok(meeting);
    },
  ],
  // Voice call history + transcripts. Joining is off in Test Mode, so only
  // reads exist here; `/segments` before the bare call so it is not taken for
  // an id.
  [
    /^\/api\/voice-calls$/,
    (r) =>
      ok({ calls: data.buildVoiceCalls(r.query.get('channelId')), transcriptionEnabled: true }),
  ],
  [
    /^\/api\/voice-calls\/[^/]+\/segments$/,
    (r) => {
      const callId = r.pathname.split('/').filter(Boolean)[2] ?? '';
      const call = data.buildVoiceCall(callId);
      return ok({ segments: call?.segments ?? [], ended: true });
    },
  ],
  [
    /^\/api\/voice-calls\/[^/]+$/,
    (r) => {
      const call = data.buildVoiceCall(lastSegment(r.pathname));
      if (!call) throw new ApiError(404, 'Voice call not found', 'VOICE_CALL_NOT_FOUND');
      return ok(call);
    },
  ],
  // Voice channels — their own entity. `/presence` before the bare list so
  // the more specific path wins, exactly as Express orders them.
  [/^\/api\/voice-channels\/presence$/, () => ok(data.buildVoicePresence())],
  [/^\/api\/voice-channels$/, () => ok({ channels: data.buildVoiceChannels() })],
  // Team chat (internal Discord-like messaging) — specific paths first.
  [/^\/api\/team-chat\/unread-summary$/, () => ok(data.buildTeamChatUnread())],
  [
    /^\/api\/team-chat\/search$/,
    (r) =>
      ok({
        results: data.buildTeamChatSearch(
          r.query.get('q') ?? '',
          r.query.get('conversationId') ?? undefined,
        ),
      }),
  ],
  [
    /^\/api\/team-chat\/conversations\/[^/]+\/messages$/,
    (r) => {
      const parts = r.pathname.split('/').filter(Boolean);
      const id = parts[parts.length - 2] ?? '';
      return ok(data.buildTeamChatMessages(id));
    },
  ],
  [
    /^\/api\/team-chat\/conversations\/[^/]+\/pins$/,
    (r) => {
      const parts = r.pathname.split('/').filter(Boolean);
      const id = parts[parts.length - 2] ?? '';
      return ok({ messages: data.buildTeamChatPins(id) });
    },
  ],
  [/^\/api\/team-chat\/conversations$/, () => ok({ conversations: data.buildTeamConversations() })],

  // Presence (online/offline dots).
  [/^\/api\/presence$/, () => ok({ onlineUserIds: data.buildOnlineUserIds() })],
  // Personal reminders — a live one fires the banner a few minutes in.
  [/^\/api\/reminders$/, () => ok(data.buildReminders())],

  // Concedii. The requests list is mutable (the demo approves and withdraws),
  // so these read the live module-level list rather than a rebuilt seed.
  [
    /^\/api\/leave\/requests$/,
    (r) => {
      const requests = data.filterLeaveRequests(r.query);
      return ok({ requests, total: requests.length });
    },
  ],
  [
    /^\/api\/leave\/balance$/,
    (r) =>
      ok({
        // No userId means "me", and in Test Mode "me" is the demo account —
        // the real route reads it off the session.
        balance: data.leaveBalance(
          r.query.get('userId') ?? data.DEMO_USER_REF.id,
          Number(r.query.get('year')) || new Date().getFullYear(),
        ),
      }),
  ],
  [
    /^\/api\/leave\/days$/,
    (r) =>
      ok({
        days: data.leaveDays(
          r.query.get('from') ?? '',
          r.query.get('to') ?? '',
          r.query.get('userId'),
        ),
      }),
  ],
  [
    /^\/api\/leave\/holidays$/,
    (r) =>
      ok({
        holidays: data.publicHolidaysMock(
          Number(r.query.get('year')) || new Date().getFullYear(),
          r.query.get('through') ? Number(r.query.get('through')) : undefined,
        ),
      }),
  ],
  [/^\/api\/leave\/pending-count$/, () => ok({ count: data.pendingLeaveCount() })],
  [
    /^\/api\/leave\/report$/,
    (r) => {
      const year = Number(r.query.get('year')) || new Date().getFullYear();
      return ok({ year, rows: data.leaveReport(year) });
    },
  ],

  // Dashboard
  // Configurable dashboard layout: no saved row in Test Mode → default layout.
  [/^\/api\/dashboard\/layout$/, () => ok(null)],
  [/^\/api\/dashboard\/summary$/, () => ok(data.buildDashboardSummary())],
  [/^\/api\/dashboard\/stats$/, () => ok(data.buildDashboardStats())],
  [
    /^\/api\/dashboard\/recent-activity$/,
    (r) => ok(data.buildRecentActivity(Number(r.query.get('limit')) || 10)),
  ],
  // COO Executive Command Center (CLE-256)
  [/^\/api\/dashboard\/kpis$/, () => ok(data.buildDashboardKpis())],
  [/^\/api\/dashboard\/today$/, () => ok(data.buildDashboardToday())],
  [
    /^\/api\/dashboard\/upcoming-payments$/,
    (r) => ok(data.buildUpcomingPayments(Number(r.query.get('days')) || 14)),
  ],
  [/^\/api\/dashboard\/team-allocation$/, () => ok(data.buildTeamAllocationGrid())],
  [
    /^\/api\/dashboard\/activity-feed$/,
    (r) => ok(data.buildActivityFeed(Number(r.query.get('limit')) || 15)),
  ],
  // Charts + per-person team tasks (CLE-257)
  [/^\/api\/dashboard\/charts$/, () => ok(data.buildDashboardCharts())],
  [/^\/api\/dashboard\/team-tasks$/, () => ok(data.buildTeamTasks())],
  [
    /^\/api\/dashboard\/overdue-invoices$/,
    (r) => ok(data.buildOverdueInvoices(r.query.get('companyId'))),
  ],

  // KB project overview — drives the project detail "Overview" tab.
  // The tab requests this with the project CODE (e.g. ECOM) or id (pr1), so we
  // resolve either. Without this route the call fell through to the empty
  // fallback and the Overview tab crashed on `data.project.status` (CLE-253).
  [
    /^\/api\/kb\/projects\/[^/]+\/overview$/,
    (r) => {
      const parts = r.pathname.split('/').filter(Boolean);
      const codeOrId = decodeURIComponent(parts[parts.length - 2] ?? '');
      const project = data.resolveProjectId(codeOrId);
      if (!project) return undefined;
      const overview = data.buildProjectOverview(
        project,
        data.tasksForProject(project).map((t) => ({ status: t.status })),
      );
      return overview ? ok(overview) : undefined;
    },
  ],

  // Companies (multi-company). The sidebar switcher + settings tab read this.
  [/^\/api\/companies$/, () => ok({ companies: data.TEST_COMPANIES })],
  // Administrators / sole associates (CLE-ADMIN): no demo mandates.
  [/^\/api\/companies\/[^/]+\/administrators$/, () => ok({ administrators: [] })],

  // Projects
  [
    /^\/api\/projects$/,
    (r) => paged(data.filterByCompany(data.buildProjectSummaries(), r.query.get('companyId'))),
  ],
  [
    /^\/api\/projects\/[^/]+\/resources\/tree$/,
    (r) => {
      // The path is /api/projects/:id/resources/tree → id is 3rd-from-last seg.
      const parts = r.pathname.split('/').filter(Boolean);
      const id = parts[parts.length - 3] ?? '';
      return ok(data.buildProjectResourcesTree(id));
    },
  ],
  // Credentials vault: Test Mode ships no fake secrets on purpose, so both the
  // entry list and the audit log answer empty instead of throwing
  // TEST_MODE_UNHANDLED every time a project opens on the Vault tab.
  [/^\/api\/projects\/[^/]+\/vault$/, () => ok([])],
  [/^\/api\/projects\/[^/]+\/vault\/audit$/, () => ok([])],
  // Project context (tab „Context”): Test Mode has no story to tell, so the
  // tab opens empty instead of throwing TEST_MODE_UNHANDLED.
  [
    /^\/api\/projects\/[^/]+\/context$/,
    (r) =>
      ok({
        document: {
          markdown: '',
          updatedAt: null,
          kbPath: `projects/${r.pathname.split('/')[3]}/context.md`,
          exists: false,
        },
        entries: [],
      }),
  ],
  [
    /^\/api\/projects\/[^/]+$/,
    (r) => {
      const id = lastSegment(r.pathname);
      const detail = data.buildProjectDetail(
        id,
        data.tasksForProject(id).map((t) => ({
          id: t.id,
          title: t.title,
          status: t.status,
          priority: t.priority,
          assigneeId: t.assigneeId,
          dueDate: t.dueDate,
        })),
      );
      return detail ? ok(detail) : undefined;
    },
  ],

  // Meeting notes — polymorphic list scoped by owner query param. Project
  // notes (CLE-255) drive the project detail "Notițe ședință" tab; lead notes
  // (CLE-261) drive the lead detail "Notițe de întâlnire" section. Client /
  // person owners return empty (their notes live in their own details).
  // Meetings module (Întâlniri): the directory and the log, with every list
  // filter honoured. Contacts before the `/:id` detail so the literal wins.
  [
    /^\/api\/meeting-log\/contacts$/,
    (r) => {
      const result = data.buildMeetingContacts(r.query.get('search'));
      return { ...ok(result.data), meta: result.meta };
    },
  ],
  [
    /^\/api\/meeting-log\/contacts\/[^/]+$/,
    (r) => {
      const contact = data.buildMeetingContact(lastSegment(r.pathname));
      if (!contact) throw new ApiError(404, 'Contact not found', 'NOT_FOUND');
      return ok(contact);
    },
  ],
  [
    /^\/api\/meeting-log$/,
    (r) => {
      const result = data.buildMeetings({
        search: r.query.get('search') ?? undefined,
        clientId: r.query.get('clientId') ?? undefined,
        projectId: r.query.get('projectId') ?? undefined,
        ownerId: r.query.get('ownerId') ?? undefined,
        contactId: r.query.get('contactId') ?? undefined,
        from: r.query.get('from') ?? undefined,
        to: r.query.get('to') ?? undefined,
        page: Number(r.query.get('page') ?? '1') || 1,
        limit: Number(r.query.get('limit') ?? '20') || 20,
        sort: (r.query.get('sort') as 'startAt' | '-startAt' | null) ?? '-startAt',
      });
      return { ...ok(result.data), meta: result.meta };
    },
  ],
  [
    /^\/api\/meeting-log\/[^/]+$/,
    (r) => {
      const meeting = data.buildMeeting(lastSegment(r.pathname));
      if (!meeting) throw new ApiError(404, 'Meeting not found', 'NOT_FOUND');
      return ok(meeting);
    },
  ],
  [/^\/api\/meeting-notes\/search$/, () => ok({ query: '', results: [] })],
  [
    /^\/api\/meeting-notes$/,
    (r) => {
      const projectId = r.query.get('projectId');
      const leadId = r.query.get('leadId');
      const clientId = r.query.get('clientId');
      const personId = r.query.get('personId');
      const meetingId = r.query.get('meetingId');
      const ingestStatus = r.query.get('ingestStatus');
      let notes: ReturnType<typeof data.meetingNotesForProject> = [];
      if (meetingId) notes = data.meetingNotesForMeeting(meetingId);
      else if (projectId) notes = data.meetingNotesForProject(projectId);
      else if (leadId) notes = data.meetingNotesForLead(leadId);
      else if (clientId) notes = data.meetingNotesForClient(clientId);
      else if (personId) notes = data.meetingNotesForPerson(personId);
      else return ok([]);
      if (ingestStatus) notes = notes.filter((n) => n.ingestStatus === ingestStatus);
      return ok(notes);
    },
  ],
  [
    /^\/api\/meeting-notes\/[^/]+$/,
    (r) => {
      const id = lastSegment(r.pathname);
      const note =
        data.buildMeetingNote(id) ?? data.buildLeadMeetingNote(id) ?? data.buildMeetingLogNote(id);
      return note ? ok(note) : undefined;
    },
  ],

  // Configurable task statuses (CLE-DASH)
  [
    /^\/api\/task-statuses$/,
    () => ok(data.buildTaskStatuses(data.buildTasks().map((t) => t.status))),
  ],

  // Departments (CLE-DASH)
  [
    /^\/api\/departments$/,
    () =>
      ok(
        data.buildDepartments(
          data.buildPeople().map((p) => p.departmentId),
          data.buildTasks().map((t) => t.departmentId),
        ),
      ),
  ],

  // Client industries (the client form's "Industrie" dropdown).
  [
    /^\/api\/client-industries$/,
    () =>
      ok(
        ['Medical', 'Pharma', 'Dental', 'Estetică & Beauty', 'HoReCa', 'IT & Software'].map(
          (name, i) => ({ id: `ind-demo-${i + 1}`, name, clientCount: 0 }),
        ),
      ),
  ],

  // Task templates (CLE-DASH)
  [/^\/api\/task-templates$/, () => ok(data.buildTaskTemplates())],

  // Email templates (overdue invoice reminders + welcome)
  [/^\/api\/email-templates$/, () => ok(data.buildEmailTemplates())],
  [
    /^\/api\/email-templates\/[^/]+$/,
    () => {
      const templates = data.buildEmailTemplates();
      return ok(templates[0]);
    },
  ],

  // Tasks
  // The header totals must honour the active list filters (CLE-435) — the
  // real endpoint recomputes per filter, so the mock reuses filterTasks().
  // Ignoring the query froze the "N sarcini · Xh" header while filtering.
  [/^\/api\/tasks\/personal-project$/, () => ok(data.PERSONAL_PROJECT)],
  [/^\/api\/tasks\/general-project$/, () => ok(data.GENERAL_PROJECT)],
  // Tag -> id. Demo tasks are never renumbered, so this only ever answers the
  // live-tag case; the fallback in TaskDetailPage covers the rest.
  [
    /^\/api\/tasks\/resolve\/[^/]+$/,
    (r) => {
      const tag = decodeURIComponent(r.pathname.split('/').pop() ?? '').toLowerCase();
      // Personal tasks (PERS-n) live in their own list; leaving them out sent
      // every click on /personal-tasks to "task not found".
      const match = [...data.buildTasks(), ...data.buildPersonalTasks()].find(
        (t) => t.identifier?.toLowerCase() === tag,
      );
      if (!match) throw new Error('Task not found');
      return ok({ id: match.id, identifier: match.identifier, renamed: false });
    },
  ],
  [/^\/api\/tasks\/totals$/, (r) => ok(buildEstimateTotals(filterTasks(r).tasks))],
  // The timer bar polls this on EVERY page, so it needs a real route: the
  // generic fallback has no `timers` key and would blank the whole app.
  [
    /^\/api\/tasks\/timers\/active$/,
    () => ok({ timers: [...demoTimers.values()], serverNow: new Date().toISOString() }),
  ],
  [/^\/api\/tasks$/, (r) => ok(filterTasks(r))],
  [
    /^\/api\/tasks\/[^/]+\/time-entries$/,
    (r) => {
      const id = personIdFrom(r.pathname, '/time-entries');
      return ok(data.buildTaskTimeEntries(id));
    },
  ],
  [
    /^\/api\/tasks\/[^/]+\/subtasks$/,
    (r) => {
      const id = personIdFrom(r.pathname, '/subtasks');
      return ok(data.buildTaskSubtasks(id));
    },
  ],
  [
    /^\/api\/tasks\/[^/]+\/comments$/,
    (r) => {
      const id = personIdFrom(r.pathname, '/comments');
      return ok(data.buildTaskComments(id));
    },
  ],
  [
    /^\/api\/tasks\/[^/]+\/activity$/,
    (r) => {
      const id = personIdFrom(r.pathname, '/activity');
      return ok(data.buildTaskActivity(id));
    },
  ],
  [
    /^\/api\/tasks\/[^/]+\/timeline$/,
    (r) => {
      const id = personIdFrom(r.pathname, '/timeline');
      return ok(data.buildTaskTimeline(id));
    },
  ],
  [
    /^\/api\/tasks\/[^/]+\/relations$/,
    () => ok({ relations: [], isBlocked: false, blockedBy: [] }),
  ],
  [
    /^\/api\/tasks\/[^/]+$/,
    (r) => {
      const t = data.findTask(lastSegment(r.pathname));
      return t ? ok(t) : undefined;
    },
  ],

  // Clients
  [
    /^\/api\/clients$/,
    (r) => paged(data.filterByCompany(data.clientSummariesWithCounts(), r.query.get('companyId'))),
  ],
  [
    /^\/api\/clients\/[^/]+\/resources\/tree$/,
    (r) => {
      const parts = r.pathname.split('/').filter(Boolean);
      const id = parts[parts.length - 3] ?? '';
      return ok(data.buildClientResourcesTree(id));
    },
  ],
  [
    /^\/api\/clients\/[^/]+\/invoices$/,
    (r) => {
      const id = personIdFrom(r.pathname, '/invoices');
      const result = data.buildClientInvoices(id);
      return { success: true, data: result.invoices, meta: { totals: result.totals } };
    },
  ],
  [
    /^\/api\/clients\/[^/]+\/stats$/,
    (r) => ok(data.buildClientStats(personIdFrom(r.pathname, '/stats'))),
  ],
  [
    /^\/api\/clients\/[^/]+$/,
    (r) => {
      const id = lastSegment(r.pathname);
      const detail = data.buildClientDetail(id, data.clientProjects(id));
      return detail ? ok(detail) : undefined;
    },
  ],

  // Suppliers
  [
    /^\/api\/suppliers$/,
    (r) => paged(data.filterByCompany(data.buildSuppliers(), r.query.get('companyId'))),
  ],
  // Nomenclator (must precede the /:id catch-all below).
  [/^\/api\/suppliers\/categories$/, () => ok(data.buildSupplierCategories())],
  [
    /^\/api\/suppliers\/[^/]+\/resources$/,
    (r) => {
      const id = personIdFrom(r.pathname, '/resources');
      return ok(data.buildSupplierResources(id));
    },
  ],
  [
    /^\/api\/suppliers\/[^/]+\/stats$/,
    (r) => ok(data.buildSupplierStats(personIdFrom(r.pathname, '/stats'))),
  ],
  [
    /^\/api\/suppliers\/[^/]+$/,
    (r) => {
      const s = data.buildSupplier(lastSegment(r.pathname));
      return s ? ok(s) : undefined;
    },
  ],

  // Leads
  [/^\/api\/leads\/pipelines$/, () => ok({ pipelines: [data.buildPipeline()] })],
  [/^\/api\/leads\/pipelines\/[^/]+$/, () => ok(data.buildPipeline())],
  [/^\/api\/leads\/board$/, () => ok(data.buildLeadBoard())],
  [/^\/api\/leads\/stats$/, () => ok(data.buildLeadStats())],
  [/^\/api\/leads\/actions\/overdue$/, () => ok({ actions: [] })],
  // Per-lead actions & interaction timeline (CLE-261). The lead id is the
  // 2nd-from-last path segment (…/leads/:id/actions).
  [
    /^\/api\/leads\/[^/]+\/actions$/,
    (r) => {
      const parts = r.pathname.split('/').filter(Boolean);
      const id = parts[parts.length - 2] ?? '';
      return ok({ actions: data.leadActions(id) });
    },
  ],
  [
    /^\/api\/leads\/[^/]+\/timeline$/,
    (r) => {
      const parts = r.pathname.split('/').filter(Boolean);
      const id = parts[parts.length - 2] ?? '';
      return ok({ timeline: data.leadTimeline(id) });
    },
  ],
  [/^\/api\/leads$/, (r) => ok(filterLeads(r))],
  [
    /^\/api\/leads\/[^/]+$/,
    (r) => {
      const l = data.buildLead(lastSegment(r.pathname));
      return l ? ok(l) : undefined;
    },
  ],

  // People
  // Cross-people allocation listing (calendar team Gantt).
  [/^\/api\/allocations$/, () => ok(teamAllocations())],
  [/^\/api\/people\/org-chart$/, () => ok(data.buildOrgChart())],
  [
    /^\/api\/people\/capacity$/,
    (r) => ok(buildDemoCapacity(r.query.get('startDate'), Number(r.query.get('weeks')) || 4)),
  ],
  [
    /^\/api\/people\/conflicts$/,
    (r) =>
      ok(
        buildDemoCapacity(r.query.get('startDate'), Number(r.query.get('weeks')) || 4).flatMap(
          (row) =>
            row.weeks
              .filter((w) => w.isOverallocated)
              .map((w) => ({
                personId: row.person.id,
                personName: row.person.fullName,
                weekStart: w.weekStart,
                weekEnd: w.weekEnd,
                totalPercent: w.totalPercent,
                overBy: w.totalPercent - 100,
                allocations: w.allocations,
              })),
        ),
      ),
  ],
  [/^\/api\/people\/[^/]+\/tasks$/, (r) => ok(personTasks(personIdFrom(r.pathname, '/tasks')))],
  [
    /^\/api\/people\/[^/]+\/employment$/,
    (r) => ok(data.buildPersonEmployment(personIdFrom(r.pathname, '/employment'))),
  ],
  [
    /^\/api\/people\/[^/]+\/payroll$/,
    (r) => ok(data.buildPersonPayroll(personIdFrom(r.pathname, '/payroll'))),
  ],
  [
    /^\/api\/people\/[^/]+\/activity$/,
    (r) => {
      const id = personIdFrom(r.pathname, '/activity');
      const tasks = data
        .buildTasks()
        .filter((t) => t.assigneeId === id)
        .map((t) => ({
          id: t.id,
          identifier: t.identifier,
          title: t.title,
          projectId: t.projectId,
          projectName: t.projectName,
          projectCode: t.identifier?.split('-')[0] ?? null,
        }));
      return ok(data.buildPersonActivity(id, tasks));
    },
  ],
  [
    /^\/api\/people\/[^/]+\/allocations$/,
    (r) => ok(allocationsFor(personIdFrom(r.pathname, '/allocations'))),
  ],
  [
    /^\/api\/people\/[^/]+\/resources$/,
    (r) => ok(data.buildPersonResources(personIdFrom(r.pathname, '/resources'))),
  ],
  [
    /^\/api\/people$/,
    (r) => {
      let people = data.peopleWithAllocations();
      const departmentId = r.query.get('departmentId');
      if (departmentId) people = people.filter((p) => p.departmentId === departmentId);
      return ok(people);
    },
  ],
  [
    /^\/api\/people\/[^/]+$/,
    (r) => {
      const p = data.peopleWithAllocations().find((x) => x.id === lastSegment(r.pathname));
      return p ? ok(p) : undefined;
    },
  ],
  // Calendar connections (CalendarIntegration component reads this).
  [/^\/api\/users\/[^/]+\/calendars\/events$/, () => ok(data.buildCalendarEvents())],
  [/^\/api\/users\/[^/]+\/calendars$/, () => ok({ connections: [], googleConfigured: false })],
  // User invoices — must return UserInvoicesResponse shape.
  [
    /^\/api\/users\/[^/]+\/invoices$/,
    (r) => {
      const id = personIdFrom(r.pathname, '/invoices');
      return ok(buildUserInvoicesResponse(id));
    },
  ],
  // Gmail connections (GmailIntegration component reads this).
  [/^\/api\/users\/[^/]+\/gmail$/, () => ok({ connections: [], googleConfigured: false })],
  [/^\/api\/users$/, () => ok(data.buildTeamUsers())],
  // Chat roster profile card. Demo user ids double as person ids, so the
  // person row and the person's workload are both reachable by the same id.
  [
    /^\/api\/users\/[^/]+\/profile$/,
    (r) => {
      const id = personIdFrom(r.pathname, '/profile');
      const person = data.buildPeople().find((p) => p.id === id);
      const workload = personTasks(id);
      // The demo user sits in every chat roster but has no Person row in the
      // seeds; the empty-fallback envelope would have no `tasks` array and the
      // card would throw on `.length`.
      return ok({
        id,
        name: person?.fullName ?? 'Demo User',
        email: person?.email ?? 'demo@firmio.ro',
        role: person ? 'MEMBER' : 'ADMIN',
        avatarUrl: null,
        isActive: true,
        createdAt: person?.createdAt ?? data.isoDateTime(-400, 9),
        lastSeenAt: null,
        person: person
          ? {
              id: person.id,
              title: person.title,
              department: person.department,
              location: person.location,
              phone: person.phone,
              startDate: person.startDate,
            }
          : null,
        tasks: workload.tasks,
        projects: workload.allocations,
        stats: workload.stats,
      });
    },
  ],

  // Invoices
  [/^\/api\/invoices$/, (r) => paged(filterInvoices(r))],
  [
    /^\/api\/invoices\/[^/]+$/,
    (r) => {
      const inv = data.buildInvoice(lastSegment(r.pathname));
      return inv ? ok(inv) : undefined;
    },
  ],

  // Financials
  // Cash flow projection + planned payments/receipts (writes are generic no-ops).
  [
    /^\/api\/financials\/cashflow$/,
    (r) => {
      const raw = Number(r.query.get('months'));
      const months = ([1, 2, 3, 6] as const).find((m) => m === raw) ?? 3;
      return ok(data.buildCashflowProjection(months));
    },
  ],
  [/^\/api\/scheduled-payments$/, () => paged(data.buildScheduledPayments())],
  [
    /^\/api\/financials\/analytics$/,
    (r) => ok(data.buildFinancialAnalytics(r.query.get('dimension') ?? 'service')),
  ],
  // Raportul financiar (Financiar → Rapoarte): orice perioadă, plus lunile închise.
  [/^\/api\/financials\/report$/, (r) => ok(data.buildFinancialReport(r.query))],
  [/^\/api\/financials\/monthly-report\/closings$/, () => ok(data.buildReportClosings())],
  [
    /^\/api\/financials\/monthly-report\/\d+\/\d+$/,
    (r) => {
      const parts = r.pathname.split('/');
      const query = new URLSearchParams(r.query);
      query.set('kind', 'month');
      query.set('year', parts[parts.length - 2]);
      query.set('month', parts[parts.length - 1]);
      return ok(data.buildFinancialReport(query));
    },
  ],
  [/^\/api\/financials\/overview$/, () => ok(data.buildFinancialOverview())],
  [/^\/api\/financials\/trends$/, () => ok(data.buildFinancialTrends())],
  [
    /^\/api\/financials\/monthly\/\d+\/\d+$/,
    (r) => {
      const parts = r.pathname.split('/');
      const year = Number(parts[parts.length - 2]);
      const month = Number(parts[parts.length - 1]);
      return ok(data.buildMonthlyPnL(year, month));
    },
  ],

  // Daily status check-ins (board / reports / settings). Specific paths first.
  [
    /^\/api\/daily-status\/me$/,
    (r) => ok(data.buildDailyStatusBoard(undefined, r.query.get('date') ?? undefined)),
  ],
  [
    /^\/api\/daily-status\/users\/[^/]+$/,
    (r) =>
      ok(data.buildDailyStatusBoard(lastSegment(r.pathname), r.query.get('date') ?? undefined)),
  ],
  [
    /^\/api\/daily-status\/report\/team$/,
    (r) => ok(data.buildTeamDailyReport(r.query.get('date') ?? undefined)),
  ],
  [
    /^\/api\/daily-status\/report$/,
    (r) =>
      ok(
        data.buildDailyStatusReport(
          r.query.get('userId') ?? undefined,
          r.query.get('date') ?? undefined,
        ),
      ),
  ],
  [/^\/api\/daily-status\/reports$/, () => ok(data.buildDailyReportHistory())],
  [/^\/api\/settings\/daily-status$/, () => ok(data.buildDailyStatusSettings())],

  // Daily / Weekly summaries
  [/^\/api\/daily\/all-users$/, () => ok(data.buildBusinessDailySummary())],
  [
    /^\/api\/daily\/[^/]+\/emails$/,
    (r) => ok(data.buildEmailSummary(personIdFrom(r.pathname, '/emails'))),
  ],
  [
    /^\/api\/daily\/[^/]+$/,
    (r) => ok(data.buildDailySummary(lastSegment(r.pathname), r.query.get('date') ?? undefined)),
  ],
  [/^\/api\/weekly$/, () => ok(data.buildWeeklySummary())],

  // AI Assistant
  [/^\/api\/chat\/status$/, () => ok({ enabled: true, model: 'claude-haiku-5-5' })],
  [
    /^\/api\/chat\/conversations\/[^/]+$/,
    (r) => {
      const conv = data.buildConversation(decodeURIComponent(lastSegment(r.pathname)));
      return conv ? ok({ conversation: conv }) : ok({ conversation: null });
    },
  ],
  [/^\/api\/chat\/conversations$/, () => ok({ conversations: data.buildConversationSummaries() })],

  // ANAF e-Factura (SPV): the demo has no SPV connection, so the page renders
  // its "not configured" state with an empty mirror instead of a 404.
  [
    /^\/api\/anaf\/efactura\/status$/,
    () =>
      ok({
        appConfigured: false,
        companyId: 'demo',
        connection: {
          state: 'not_configured',
          message: 'Contul demo nu are conexiune SPV.',
          cif: null,
          env: 'prod',
          tokenExpiresAt: null,
          authorizedBy: null,
          messagesLastDay: null,
        },
        cachedMessages: 0,
        lastSyncAt: null,
      }),
  ],
  [/^\/api\/anaf\/efactura\/companies$/, () => ok({ companies: [] })],
  [
    /^\/api\/anaf\/efactura\/messages$/,
    () => ({
      success: true,
      data: [],
      meta: { page: 1, limit: 50, total: 0, totalPages: 1 },
      totals: [],
    }),
  ],

  // Facturi emise: the demo issues nothing, so the list is empty and the
  // reference data lets the editor render.
  [
    /^\/api\/issued-invoices$/,
    () => ok({ data: [], meta: { page: 1, limit: 50, total: 0, pages: 1 }, totals: [] }),
  ],
  [
    /^\/api\/issued-invoices\/series$/,
    () =>
      ok([
        {
          id: 'demo-series',
          prefix: 'FIRMIO',
          nextNumber: 1,
          padding: 4,
          isDefault: true,
          nextFullNumber: 'FIRMIO 0001',
        },
      ]),
  ],
  [/^\/api\/issued-invoices\/units$/, () => ok([{ code: 'H87', label: 'buc', plural: 'bucăți' }])],
  [/^\/api\/issued-invoices\/counties$/, () => ok([{ code: 'RO-B', name: 'București' }])],

  // SmartBill
  [
    /^\/api\/smartbill\/status$/,
    () =>
      ok({
        configured: true,
        connection: { ok: true, code: 'OK', message: 'Conectat (Test Mode)' },
        cachedInvoices: 0,
        lastSyncAt: data.isoDateTime(-1, 9),
      }),
  ],
  [/^\/api\/smartbill\/summary$/, () => ok(data.buildSmartbillSummary())],
  [
    /^\/api\/smartbill\/invoices$/,
    (r) => {
      let invoices = data.buildSmartbillInvoices();
      const direction = r.query.get('direction');
      const status = r.query.get('paymentStatus');
      const search = r.query.get('search');
      if (direction) invoices = invoices.filter((i) => i.direction === direction);
      if (status) invoices = invoices.filter((i) => i.paymentStatus === status);
      if (search) {
        const q = search.toLowerCase();
        invoices = invoices.filter(
          (i) => i.clientName.toLowerCase().includes(q) || i.number.includes(q),
        );
      }
      return paged(invoices);
    },
  ],

  // Bank statements — populated with realistic statement summaries (demo audit).
  [/^\/api\/bank-statements\/[^/]+\/candidates$/, () => ok(data.buildReconCandidates())],
  [/^\/api\/bank-statements\/candidates$/, () => ok(data.buildReconCandidates())],
  [
    /^\/api\/bank-statements\/[^/]+$/,
    (r) => ok(data.buildBankStatementDetail(lastSegment(r.pathname))),
  ],
  [
    /^\/api\/bank-statements$/,
    () => {
      const rows = data.buildBankStatementSummaries();
      const totals = rows.reduce(
        (acc, s) => ({
          credits: acc.credits + (s.totalCredits ?? 0),
          debits: acc.debits + (s.totalDebits ?? 0),
        }),
        { credits: 0, debits: 0 },
      );
      return ok({ rows, totals, meta: { page: 1, limit: 20, total: rows.length, totalPages: 1 } });
    },
  ],

  // Bank accounts — listBankAccounts() unwraps res.data.accounts
  [/^\/api\/bank-accounts$/, () => ok({ accounts: data.buildBankAccounts() })],

  // Calendar — TodaysEventsCard (dashboard) + Daily Summary calendar section.
  // MUST return the CalendarEventsResponse object shape, not an array, or the
  // consumers throw on `data.events.length` and blank the page (CLE-252).
  [/^\/api\/calendar\/events$/, () => ok(data.buildCalendarEvents())],

  // Subscriptions
  // Nomenclator (must precede the /:id catch-all below).
  [/^\/api\/subscriptions\/categories$/, () => ok(data.buildSubscriptionCategories())],
  [
    /^\/api\/subscriptions\/[^/]+\/stats$/,
    (r) => ok(data.buildSubscriptionStats(personIdFrom(r.pathname, '/stats'))),
  ],
  [
    /^\/api\/subscriptions\/[^/]+$/,
    (r) => {
      const s = data.buildSubscription(lastSegment(r.pathname));
      return s ? ok(s) : undefined;
    },
  ],
  [
    /^\/api\/subscriptions$/,
    (r) => {
      let subs = data.buildSubscriptions();
      const search = r.query.get('search');
      const category = r.query.get('category');
      const active = r.query.get('active');
      if (search) {
        const q = search.toLowerCase();
        subs = subs.filter((s) => s.name.toLowerCase().includes(q));
      }
      if (category) subs = subs.filter((s) => s.category === category);
      if (active === 'true') subs = subs.filter((s) => s.isActive);
      if (active === 'false') subs = subs.filter((s) => !s.isActive);
      return paged(subs);
    },
  ],

  // Task Reports (7 endpoints)
  [/^\/api\/reports\/tasks\/overview$/, () => ok(data.buildReportOverview())],
  [/^\/api\/reports\/tasks\/hours$/, () => ok(data.buildReportHours())],
  [/^\/api\/reports\/tasks\/overruns$/, () => ok(data.buildReportOverruns())],
  [/^\/api\/reports\/tasks\/throughput$/, () => ok(data.buildReportThroughput())],
  [/^\/api\/reports\/tasks\/cycle-time$/, () => ok(data.buildReportCycleTime())],
  [/^\/api\/reports\/tasks\/workload$/, () => ok(data.buildReportWorkload())],
  [/^\/api\/reports\/tasks\/overdue$/, () => ok(data.buildReportOverdue())],

  // Settings & Configuration
  // listIntegrations() unwraps res.data.integrations. `?scope=` must be honoured
  // like the real route: the Settings tab fetches 'global' and 'user'
  // separately and concatenates, so ignoring the filter would render every
  // integration twice (with duplicate React keys).
  [
    /^\/api\/settings\/integrations$/,
    (r) => {
      const scope = r.query.get('scope');
      const all = data.buildSettingsIntegrations();
      return ok({ integrations: scope ? all.filter((i) => i.scope === scope) : all });
    },
  ],
  // getIntegration(key) unwraps res.data.integration
  [
    /^\/api\/settings\/integrations\/[^/]+$/,
    (r) => {
      const key = lastSegment(r.pathname);
      const integration = data.buildSettingsIntegration(key);
      return integration ? ok({ integration }) : undefined;
    },
  ],
  [/^\/api\/settings\/bank-defaults$/, () => ok(data.buildBankDefaults())],
  // Full-backup card: a couple of "nightly" dumps so the list isn't empty.
  [
    /^\/api\/settings\/backup$/,
    () =>
      ok({
        driveConfigured: true,
        running: false,
        driveError: null,
        schedule: (() => {
          const next = new Date();
          next.setHours(18, 0, 0, 0);
          if (next.getTime() <= Date.now()) next.setDate(next.getDate() + 1);
          const last = new Date(next);
          last.setDate(last.getDate() - 1);
          return {
            enabled: true,
            time: '18:00',
            timeZone: 'Europe/Bucharest',
            nextRunAt: next.toISOString(),
            lastRun: {
              at: last.toISOString(),
              ok: true,
              error: null,
              databaseKey: 'db-backups/app_demo.sql.gz',
              knowledgeBaseKey: 'db-backups/kb_demo.zip',
              durationMs: 5100,
            },
          };
        })(),
        backups: [0, 1].map((daysAgo) => {
          const d = new Date();
          d.setDate(d.getDate() - daysAgo);
          d.setHours(2, 30, 0, 0);
          const stamp = d
            .toISOString()
            .replace(/[-:]/g, '')
            .replace(/\.\d{3}Z$/, 'Z');
          return {
            key: `db-backups/app_${stamp}.sql.gz`,
            name: `app_${stamp}.sql.gz`,
            size: 18_400_000 - daysAgo * 120_000,
            modifiedTime: d.toISOString(),
          };
        }),
      }),
  ],
  [/^\/api\/services$/, () => ok(data.buildServices())],
  // listExpenseCategories() unwraps res.data.categories
  [/^\/api\/expense-categories$/, () => ok({ categories: data.buildExpenseCategories() })],
  // getCompanyProfile() unwraps res.data.profile
  [/^\/api\/company-profile$/, () => ok({ profile: data.buildCompanyProfile() })],
  [/^\/api\/onboarding\/status$/, () => ok(data.buildOnboardingStatus())],
  [/^\/api\/onboarding\/integrations$/, () => ok(data.buildOnboardingIntegrations())],
  [
    /^\/api\/ai-usage$/,
    (r) => {
      const from = r.query.get('from');
      const to = r.query.get('to');
      const month = r.query.get('month');
      const period =
        from && to
          ? ({ kind: 'range', from, to } as const)
          : month
            ? ({ kind: 'month', month } as const)
            : undefined;
      return ok(data.buildAiUsage(period));
    },
  ],
  // listLabels() unwraps res.data.labels
  [/^\/api\/labels$/, () => ok({ labels: data.TEST_LABELS.map((l) => ({ ...l, taskCount: 0 })) })],

  // Permissions (RBAC) — the demo account is a full-access ADMIN. Returning the
  // full matrix lets every gated card / sidebar entry / button render and keeps
  // the console clean (previously this path threw TEST_MODE_UNHANDLED). CLE-252.
  [/^\/api\/me\/permissions$/, () => ok(data.buildPermissionMatrix())],
  [/^\/api\/role-templates$/, () => ok(data.buildRoleTemplates())],
  [/^\/api\/role-templates\/system\/deleted$/, () => ok([])],
  // Settings → Module: every module on in the demo.
  [/^\/api\/modules$/, () => ok({ modules: [] })],

  // Roles / settings without a dedicated dataset fall through to graceful
  // defaults below.
];

// --- Filter helpers (mirror the real list endpoints' query semantics) ------

function filterTasks(r: ParsedRequest) {
  const status = r.query.get('status');
  const priority = r.query.get('priority');
  const assigneeId = r.query.get('assigneeId');
  const projectId = r.query.get('projectId');
  // Like the backend, the hidden personal project never leaks into the
  // company-wide list; it is only served when asked for by its own id.
  let tasks =
    projectId === data.PERSONAL_PROJECT.id ? data.buildPersonalTasks() : data.buildTasks();
  const departmentId = r.query.get('departmentId');
  const search = r.query.get('search');
  if (status) {
    const set = new Set(status.split(','));
    tasks = tasks.filter((t) => set.has(t.status));
  }
  if (priority) {
    const set = new Set(priority.split(','));
    tasks = tasks.filter((t) => set.has(t.priority));
  }
  if (departmentId) {
    const set = new Set(departmentId.split(','));
    tasks = tasks.filter((t) => t.departmentId != null && set.has(t.departmentId));
  }
  if (assigneeId) tasks = tasks.filter((t) => t.assigneeId === assigneeId);
  if (projectId) tasks = tasks.filter((t) => t.projectId === projectId);
  // Due-date window (calendar month grid): string-compare on the date part so
  // month navigation actually narrows the mock data.
  const dueFrom = r.query.get('dueFrom');
  const dueTo = r.query.get('dueTo');
  if (dueFrom) tasks = tasks.filter((t) => t.dueDate != null && t.dueDate.slice(0, 10) >= dueFrom);
  if (dueTo) tasks = tasks.filter((t) => t.dueDate != null && t.dueDate.slice(0, 10) <= dueTo);
  if (search) {
    const q = search.toLowerCase();
    // Match the real backend semantics (routes/tasks.ts): search hits the
    // human identifier as well as the title. CLE-274 — the task-detail page
    // resolves a human identifier (e.g. "CLE-1") by searching here, so without
    // identifier matching every task link would resolve to "not found".
    tasks = tasks.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        (t.identifier ? t.identifier.toLowerCase().includes(q) : false),
    );
  }
  return {
    tasks,
    pagination: { page: 1, pageSize: tasks.length || 1, total: tasks.length, totalPages: 1 },
  };
}

function filterLeads(r: ParsedRequest) {
  let leads = data.buildLeads();
  const pipelineId = r.query.get('pipelineId');
  const stageId = r.query.get('stageId');
  const assigneeIds = r.query.get('assigneeId')?.split(',') ?? [];
  if (pipelineId) leads = leads.filter((l) => l.pipelineId === pipelineId);
  if (stageId) leads = leads.filter((l) => l.stageId === stageId);
  if (assigneeIds.length) leads = leads.filter((l) => assigneeIds.includes(l.assigneeId ?? ''));
  return {
    leads,
    pagination: { page: 1, pageSize: leads.length || 1, total: leads.length, totalPages: 1 },
  };
}

function filterInvoices(r: ParsedRequest) {
  let invoices = data.filterByCompany(data.buildInvoices(), r.query.get('companyId'));
  const type = r.query.get('type');
  const status = r.query.get('status');
  const projectId = r.query.get('projectId');
  if (type) invoices = invoices.filter((i) => i.direction === type);
  if (status) invoices = invoices.filter((i) => i.status === status);
  if (projectId) invoices = invoices.filter((i) => i.project?.id === projectId);
  return invoices;
}

function buildEstimateTotals(tasks = data.buildTasks()) {
  const total = { hours: tasks.reduce((s, t) => s + (t.estimate ?? 0), 0), tasks: tasks.length };
  return { total, byStatus: [], byAssignee: [], byProject: [] };
}

function buildUserInvoicesResponse(userId: string) {
  // Match person invoices into the UserInvoicesResponse shape expected by
  // the PersonInvoicesTab (lib/userInvoicesApi.ts).
  const rawInvoices = data.buildPersonInvoices(userId);
  const invoices = rawInvoices.map((inv) => ({
    id: inv.id,
    source: 'manual' as const,
    invoiceNumber: inv.invoiceNumber,
    flow: inv.direction === 'INCOME' ? ('income' as const) : ('outcome' as const),
    description: inv.notes ?? null,
    category: inv.project?.name ?? null,
    status: inv.status,
    currency: inv.currency,
    net: inv.amount,
    vat: inv.taxAmount,
    total: inv.amountTotal,
    issueDate: inv.issueDate,
    dueDate: inv.dueDate,
    paidAt: inv.paidAt ?? (inv.status === 'PAID' ? inv.issueDate : null),
    pdfUrl: null,
    fileName: inv.fileName ?? null,
  }));
  const income = invoices.filter((i) => i.flow === 'income');
  const outcome = invoices.filter((i) => i.flow === 'outcome');
  const paid = invoices.filter((i) => i.status === 'PAID');
  const pending = invoices.filter((i) => i.status !== 'PAID');
  return {
    invoices,
    summary: {
      totalPaid: paid.reduce((s, i) => s + i.total, 0),
      totalPending: pending.reduce((s, i) => s + i.total, 0),
      totalIncome: income.reduce((s, i) => s + i.total, 0),
      totalOutcome: outcome.reduce((s, i) => s + i.total, 0),
      count: invoices.length,
      byCategory: [],
    },
    meta: { page: 1, limit: invoices.length || 25, total: invoices.length, totalPages: 1 },
  };
}

function personIdFrom(pathname: string, suffix: string): string {
  const parts = pathname.replace(suffix, '').split('/').filter(Boolean);
  return parts[parts.length - 1] ?? '';
}

function allocationsFor(personId: string) {
  const person = data.peopleWithAllocations().find((p) => p.id === personId);
  return person?.allocations ?? [];
}

// GET /api/allocations — flatten every person's allocations with a person
// summary, mirroring the backend's cross-people listing (team Gantt).
function teamAllocations() {
  return data
    .peopleWithAllocations()
    .filter((p) => p.isActive)
    .flatMap((p) =>
      p.allocations.map((alloc) => ({
        ...alloc,
        person: { id: p.id, fullName: p.fullName, capacity: p.capacity },
      })),
    );
}

function personTasks(personId: string) {
  // Mirror the backend: only open (non-terminal) tasks; byStatus keyed by slug.
  const tasks = data
    .buildTasks()
    .filter(
      (t) =>
        t.assigneeId === personId &&
        t.statusCategory !== 'COMPLETED' &&
        t.statusCategory !== 'CANCELLED',
    );
  const byStatus: Record<string, number> = {};
  for (const t of tasks) byStatus[t.status] = (byStatus[t.status] ?? 0) + 1;
  return {
    source: 'local' as const,
    tasks: tasks.map((t) => ({
      id: t.id,
      identifier: t.identifier,
      title: t.title,
      status: t.status,
      statusLabel: t.statusLabel,
      statusColor: t.statusColor,
      statusCategory: t.statusCategory,
      priority: t.priority,
      priorityValue: t.priorityValue,
      dueDate: t.dueDate,
      labels: t.labels.map((l) => ({ id: l.id, name: l.name, color: l.color })),
      url: null,
      projectId: t.projectId,
      projectName: t.projectName,
      projectCode: null,
    })),
    allocations: [],
    stats: {
      totalActive: tasks.length,
      overdue: 0,
      inProgress: tasks.filter((t) => t.statusCategory === 'STARTED').length,
      activeProjects: new Set(tasks.map((t) => t.projectId)).size,
      byStatus,
      byPriority: {},
    },
    labels: data.TEST_LABELS.map((l) => ({ id: l.id, name: l.name, color: l.color })),
  };
}

// --- Entry point -----------------------------------------------------------

/**
 * Resolve a request against the simulated dataset. Returns the response body
 * (already in the right envelope shape) for GETs, a benign success for writes,
 * and a graceful empty fallback for unmatched GETs.
 */
export function mockApiFetch<T>(path: string, init?: RequestInit): T {
  const req = parse(path, init);

  if (req.method !== 'GET') {
    // Team chat writes need shaped responses — the mutations unwrap
    // `res.data.message` / `res.data.conversation` and would crash on the
    // generic `{ simulated }` no-op below.
    if (req.pathname.startsWith('/api/team-chat/')) {
      return ok(data.mockTeamChatWrite(req.pathname, req.method, init?.body)) as T;
    }
    // Voice is the one feature Test Mode cannot fake. Everything else here is
    // a JSON shape; a call is a real microphone and a real WebRTC connection to
    // a real LiveKit server. Answering 503 — the same answer a server without
    // LIVEKIT_* gives — makes the client show "voice unavailable" instead of
    // calling `room.connect(undefined, undefined)` and throwing. The join
    // button is disabled here anyway; this is the backstop.
    if (/^\/api\/voice-channels\/[^/]+\/token$/.test(req.pathname)) {
      throw new ApiError(503, 'Voice is not available in Test Mode', 'LIVEKIT_NOT_CONFIGURED');
    }
    // Daily status check-ins: the mutation re-applies the returned task to the
    // board, so the response has to be a TodayTask, not the generic no-op.
    if (req.pathname.startsWith('/api/daily-status/me/tasks/')) {
      const body = init?.body ? (JSON.parse(String(init.body)) as unknown) : {};
      return ok(data.mockDailyStatusWrite(req.pathname, req.method, body)) as T;
    }
    // Task timers keep their state (demoTimers below): Start / Pause / Stop
    // behave like the real thing, the Android timer notification included.
    const timerResult = demoTimerWrite(req.pathname);
    if (timerResult !== undefined) return ok(timerResult) as T;
    // Bug widget: the success screen names the filed report ("BUG-…"); the
    // generic no-op had no identifier and read "undefined".
    if (req.pathname === '/api/bug-reports' && req.method === 'POST') {
      return ok({ id: `demo-bug-${Date.now()}`, identifier: 'BUG-DEMO' }) as T;
    }
    if (req.pathname.startsWith('/api/reminders')) {
      const body = init?.body ? (JSON.parse(String(init.body)) as unknown) : {};
      return ok(data.mockReminderWrite(req.pathname, req.method, body)) as T;
    }
    // Concedii writes: the page re-renders from the returned row, so these
    // have to answer with a real LeaveRequest rather than the generic no-op.
    if (req.pathname.startsWith('/api/leave/')) {
      const body = init?.body ? (JSON.parse(String(init.body)) as Record<string, unknown>) : {};
      return ok(data.mockLeaveWrite(req.pathname, req.method, body)) as T;
    }
    if (req.method === 'POST' && req.pathname === '/api/voice-channels') {
      const body = (init?.body ? JSON.parse(String(init.body)) : {}) as { name?: string };
      return ok({
        id: `vc-${Date.now()}`,
        name: body.name ?? '',
        createdAt: new Date().toISOString(),
      }) as T;
    }
    // Specific POST handlers that require shaped responses (modal pre-fills, etc.)
    if (req.pathname === '/api/invoice-reminders/preview') {
      return ok({
        subject: 'Facturi cu scadenta depasita — Firmio Demo SRL',
        bodyHtml:
          '<p>Buna ziua,</p><p>Va reamintim ca aveti facturi cu scadenta depasita.</p><p>Cu stima,<br/>Echipa Firmio Demo</p>',
        to: 'contact@example.com',
        emailCandidates: ['contact@example.com', 'finance@example.com'],
        invoiceIds: ['inv-in-1001'],
        invoiceCount: 1,
        totalOverdue: 12500,
        currency: 'RON',
      }) as T;
    }
    // "Back up now": the card reads `database` / `knowledgeBase` off the
    // result, which the generic `{ simulated }` no-op below would not carry.
    if (req.pathname === '/api/settings/backup/run') {
      const now = new Date();
      const stamp = now
        .toISOString()
        .replace(/[-:]/g, '')
        .replace(/\.\d{3}Z$/, 'Z');
      return ok({
        startedAt: new Date(now.getTime() - 4200).toISOString(),
        finishedAt: now.toISOString(),
        durationMs: 4200,
        database: { key: `db-backups/app_${stamp}.sql.gz`, fileId: 'demo-db', bytes: 18_400_000 },
        knowledgeBase: {
          key: `db-backups/kb_${stamp}.zip`,
          fileId: 'demo-kb',
          bytes: 2_300_000,
          files: 148,
        },
      }) as T;
    }
    if (req.pathname === '/api/invoice-reminders/send') {
      return ok({ sent: true, messageId: 'demo-msg-1', sentAt: new Date().toISOString() }) as T;
    }
    // Statement deletion: the page interpolates these counters into the notice.
    if (req.method === 'DELETE' && /^\/api\/bank-statements\/[^/]+$/.test(req.pathname)) {
      return ok({ deletedTransactions: 0, deletedFinancials: 0, revertedInvoices: 0 }) as T;
    }
    // Integration credential deletion. The generic write no-op below answers
    // `{ id, simulated: true }`, so the caller reads `res.data.integration` as
    // undefined and the card blows up re-rendering `integration.fields` — i.e.
    // clearing a credential crashed the UI in Test Mode. Hand back a real
    // integration view instead. (Nothing is actually deleted: the dataset is
    // rebuilt from the seeds on every call.)
    if (req.method === 'DELETE' && /^\/api\/settings\/integrations$/.test(req.pathname)) {
      return ok({ deleted: 0 }) as T;
    }
    if (
      req.method === 'DELETE' &&
      /^\/api\/settings\/integrations\/[^/]+(\/[^/]+)?$/.test(req.pathname)
    ) {
      // `/:key` and `/:key/:field` both answer with the integration view; the
      // key is the segment right after "integrations".
      const key = req.pathname.split('/')[4] ?? '';
      const integration = data.buildSettingsIntegration(key);
      return ok({ integration: integration ?? null }) as T;
    }
    // Settings → Module reads the whole payload back; the generic no-op has none.
    if (req.method === 'PUT' && req.pathname === '/api/modules') {
      const body = (init?.body ? JSON.parse(String(init.body)) : {}) as {
        disabled?: ModuleKey[];
      };
      return ok(demoModules(body.disabled ?? [])) as T;
    }
    // Task templates: the settings editor reads the created/updated template
    // back (id, items with labels), which the generic no-op cannot provide.
    if (
      (req.method === 'POST' && req.pathname === '/api/task-templates') ||
      (req.method === 'PUT' && /^\/api\/task-templates\/[^/]+$/.test(req.pathname))
    ) {
      const body = (init?.body ? JSON.parse(String(init.body)) : {}) as {
        name?: string;
        description?: string | null;
        departmentId?: string | null;
        items?: Array<{
          title: string;
          description?: string | null;
          priority?: string;
          estimate?: number | null;
          departmentId?: string | null;
          labelIds?: string[];
        }>;
      };
      const id = req.method === 'PUT' ? lastSegment(req.pathname) : `tpl-${Date.now()}`;
      const items = (body.items ?? []).map((it, i) => ({
        id: `${id}-item-${i}`,
        title: it.title,
        description: it.description ?? null,
        priority: it.priority ?? 'NONE',
        estimate: it.estimate ?? null,
        departmentId: it.departmentId ?? null,
        department: null,
        labels: data.TEST_LABELS.filter((l) => (it.labelIds ?? []).includes(l.id)),
        order: i,
      }));
      return ok({
        id,
        name: body.name ?? '',
        description: body.description ?? null,
        departmentId: body.departmentId ?? null,
        department: null,
        itemCount: items.length,
        items,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }) as T;
    }
    // Duplicate task: the page navigates to the copy's identifier, so hand
    // back a real Task shape — the source, re-keyed — instead of the no-op.
    if (req.method === 'POST' && /^\/api\/tasks\/[^/]+\/duplicate$/.test(req.pathname)) {
      const parts = req.pathname.split('/').filter(Boolean);
      const sourceId = parts[parts.length - 2] ?? '';
      const source = data.buildTasks().find((x) => x.id === sourceId) ?? data.buildTasks()[0];
      const body = (init?.body ? JSON.parse(String(init.body)) : {}) as {
        title?: string;
        projectId?: string;
        includeSubtasks?: boolean;
      };
      const project = body.projectId
        ? data.buildProjectSummaries().find((p) => p.id === body.projectId)
        : null;
      return ok({
        ...source,
        id: `${source.id}-copy`,
        identifier: `${project?.code ?? source.identifier?.split('-')[0] ?? 'TASK'}-999`,
        title: body.title ?? `${source.title} (copie)`,
        projectId: project?.id ?? source.projectId,
        projectName: project?.name ?? source.projectName,
        subIssueCount: body.includeSubtasks ? source.subIssueCount : 0,
        subIssueDoneCount: 0,
        commentCount: 0,
        duplicatedSubtasks: body.includeSubtasks ? source.subIssueCount : 0,
      }) as T;
    }
    // Writes are no-ops in Test Mode: never touch the real backend, never error.
    return ok({ id: lastSegment(req.pathname), simulated: true }) as T;
  }

  for (const [pattern, handler] of GET_ROUTES) {
    if (pattern.test(req.pathname)) {
      const result = handler(req);
      if (result !== undefined) return result as T;
    }
  }

  // Auth endpoints must never be intercepted by Test Mode (they're needed before
  // Test Mode is meaningful and are routed to the real backend in api.ts). If one
  // somehow reaches here, surface a clear error rather than corrupt the session.
  if (req.pathname.startsWith('/api/auth')) {
    throw new ApiError(404, `Test Mode does not simulate ${req.pathname}`, 'TEST_MODE_UNHANDLED');
  }

  // Unknown GET — degrade gracefully. We don't know whether the caller expects
  // an array or an object, so return an envelope whose `data` is BOTH: an empty
  // array that also exposes the empty-object keys consumers commonly read
  // (events, conflicts, tasks, items, …). This prevents a missing route from
  // throwing a TypeError mid-render and blanking the page (CLE-252).
  // eslint-disable-next-line no-console
  console.warn(`[TestMode] Unhandled GET ${req.pathname} — returning empty fallback.`);
  return ok(emptyFallback()) as T;
}

// An empty value that is safe to read as either an array or one of the common
// object envelopes. It IS an array (length 0) but also carries empty-object
// fields so `data.events`, `data.conflicts`, `data.tasks`, etc. never throw.
function emptyFallback(): unknown[] & Record<string, unknown> {
  const arr = [] as unknown as unknown[] & Record<string, unknown>;
  Object.assign(arr, {
    items: [],
    events: [],
    conflicts: [],
    tasks: [],
    leads: [],
    users: [],
    projects: [],
    statements: [],
    actions: [],
    timeline: [],
    timers: [],
    activity: [],
    comments: [],
    relations: [],
    permissions: {},
    // Common envelope wrappers used by nested-unwrap API clients.
    accounts: [],
    categories: [],
    integrations: [],
    companies: [],
    pipelines: [],
    templates: [],
    people: [],
    clients: [],
    suppliers: [],
    subscriptions: [],
    invoices: [],
    rows: [],
    profile: null,
    integration: null,
    pagination: { page: 1, pageSize: 1, total: 0, totalPages: 1 },
    meta: { page: 1, limit: 1, total: 0, totalPages: 1 },
    totalEvents: 0,
    lastSyncAt: null,
  });
  return arr;
}

/**
 * Capacity grid for the demo (Echipă → Planificare capacitate): the first
 * eight people, each on one or two projects, with a deterministic weekly
 * swing so the bars differ and one week runs over 100%.
 */
function buildDemoCapacity(startDate: string | null, weeks: number) {
  const projects = data.PROJECT_SEEDS.slice(0, 5);
  const start = new Date(`${startDate ?? new Date().toISOString().slice(0, 10)}T00:00:00Z`);
  // Weeks start on Monday, like the real endpoint.
  start.setUTCDate(start.getUTCDate() - ((start.getUTCDay() + 6) % 7));
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return data
    .buildPeople()
    .slice(0, 8)
    .map((person, pi) => {
      const capacity = person.capacity || 40;
      const main = projects[pi % projects.length]!;
      const side = projects[(pi + 2) % projects.length]!;
      return {
        person: { id: person.id, fullName: person.fullName, capacity },
        weeks: Array.from({ length: weeks }, (_, wi) => {
          const from = new Date(start);
          from.setUTCDate(start.getUTCDate() + wi * 7);
          const to = new Date(from);
          to.setUTCDate(from.getUTCDate() + 6);
          const mainPct = 40 + ((pi * 13 + wi * 17) % 45);
          const sidePct = pi % 3 === 0 ? 0 : 10 + ((pi * 7 + wi * 11) % 35);
          const allocations = [
            { projectId: main.id, projectName: main.name, percentage: mainPct },
            ...(sidePct
              ? [{ projectId: side.id, projectName: side.name, percentage: sidePct }]
              : []),
          ].map((a) => ({ ...a, hours: Math.round((a.percentage / 100) * capacity) }));
          const totalPercent = allocations.reduce((sum, a) => sum + a.percentage, 0);
          return {
            weekStart: iso(from),
            weekEnd: iso(to),
            allocations,
            totalPercent,
            totalHours: allocations.reduce((sum, a) => sum + a.hours, 0),
            utilizationPercent: totalPercent,
            isOverallocated: totalPercent > 100,
          };
        }),
      };
    });
}

function demoModules(disabled: ModuleKey[]) {
  const off = new Set(disabled);
  return {
    consoleLinked: false,
    modules: MODULE_CATALOG.map((m) => ({
      key: m.key,
      parent: m.parent ?? null,
      core: Boolean(m.core),
      // Opt-in modules (built for one customer) stay off in the demo.
      enabled: !m.optIn && (m.core || (!off.has(m.key) && !(m.parent && off.has(m.parent)))),
      allowedByConsole: !m.optIn,
      optIn: Boolean(m.optIn),
      disabledLocally: !m.core && off.has(m.key),
    })),
  };
}

/** One open warning and one resolved alert, so Settings → Alerte has content. */
function demoAlerts() {
  const hoursAgo = (h: number) => new Date(Date.now() - h * 3_600_000).toISOString();
  return {
    open: [
      {
        id: 'alert-1',
        key: 'drive.quota',
        service: 'drive',
        severity: 'WARNING',
        title: 'Spațiul Google Drive aproape plin',
        message: 'Au mai rămas sub 10% din spațiul de stocare al contului conectat.',
        details: null,
        count: 3,
        firstSeenAt: hoursAgo(30),
        lastSeenAt: hoursAgo(2),
        acknowledgedAt: null,
        acknowledgedBy: null,
        resolvedAt: null,
      },
    ],
    resolved: [
      {
        id: 'alert-2',
        key: 'smtp.auth',
        service: 'smtp',
        severity: 'CRITICAL',
        title: 'Autentificare SMTP eșuată',
        message: 'Serverul de email a respins parola; emailurile nu au plecat.',
        details: null,
        count: 1,
        firstSeenAt: hoursAgo(80),
        lastSeenAt: hoursAgo(80),
        acknowledgedAt: hoursAgo(79),
        acknowledgedBy: { id: 'p1', name: 'Mihai Georgescu' },
        resolvedAt: hoursAgo(78),
      },
    ],
  };
}

/** A handful of sent emails for Settings → Istoric emailuri. */
function demoEmails() {
  const hoursAgo = (h: number) => new Date(Date.now() - h * 3_600_000).toISOString();
  const rows = [
    [
      'contact@fashionro.ro',
      'Facturi cu scadența depășită — FashionRO SRL',
      'invoice-reminder',
      'SENT',
      2,
    ],
    ['echipa@firmio.ro', 'Raportul zilnic al echipei', 'daily-report', 'SENT', 0],
    [
      'office@fintech.ro',
      'Facturi cu scadența depășită — FinTech Partners SA',
      'invoice-reminder',
      'SENT',
      0,
    ],
    ['andrei.pata@cleancodeculture.ro', 'Bine ai venit în Firmio', 'welcome', 'FAILED', 0],
  ] as const;
  return rows.map(([to, subject, template, status, opens], i) => ({
    id: `email-${i + 1}`,
    to,
    subject,
    template,
    status,
    sentAt: hoursAgo(5 + i * 9),
    openCount: opens,
    firstOpenedAt: opens ? hoursAgo(4 + i * 9) : null,
    lastOpenedAt: opens ? hoursAgo(3 + i * 9) : null,
    metadata: null,
    trackingId: `trk-${i + 1}`,
    createdAt: hoursAgo(5 + i * 9),
    updatedAt: hoursAgo(5 + i * 9),
  }));
}
